import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, BookOpen, Search, Send, Loader2, RefreshCw, Printer, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export default function DeskCustomerImport() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState("");
  const [custResult, setCustResult] = useState(null);
  const [dictResult, setDictResult] = useState(null);
  const [backfill, setBackfill] = useState(null);
  const [subImport, setSubImport] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [detectResult, setDetectResult] = useState(null);
  const [pending, setPending] = useState([]);
  const [selPending, setSelPending] = useState(() => new Set());
  const [busy2, setBusy2] = useState("");
  const [rejected, setRejected] = useState([]);
  const [selRejected, setSelRejected] = useState(() => new Set());
  const [showRejected, setShowRejected] = useState(false);

  const loadSyncStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/desk-dict-sync/status`, authHeaders());
      setSyncStatus(res.data || null);
    } catch (e) { /* ignore */ }
  };
  const loadPending = async () => {
    try {
      const res = await axios.get(`${API}/admin/dict-pending`, authHeaders());
      setPending(res.data.items || []);
      setSelPending(new Set());
    } catch (e) { /* ignore */ }
  };

  const runBackfill = async (apply) => {
    if (apply && !window.confirm("既存の全顧客について、片方の単位だけ登録されているグループの兄弟単位を一括で補完します。よろしいですか？")) return;
    setBusy(apply ? "backfill-apply" : "backfill-preview");
    try {
      const res = await axios.post(`${API}/admin/backfill-unit-siblings?apply=${apply}`, {}, authHeaders());
      setBackfill(res.data);
      toast.success(res.data.message || (apply ? "補完しました" : "確認しました"));
    } catch (e) {
      toast.error("処理に失敗しました");
    } finally {
      setBusy("");
    }
  };

  const runSubImport = async (apply) => {
    setBusy(apply ? "sub-apply" : "sub-preview");
    try {
      const res = await axios.post(`${API}/admin/products/subdisplay-import?apply=${apply}`, {}, authHeaders());
      setSubImport(res.data);
      toast.success(res.data.message || (apply ? "反映しました" : "確認しました"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "処理に失敗しました");
    } finally {
      setBusy("");
    }
  };

  const runDetect = async () => {
    setBusy2("detect");
    try {
      const res = await axios.post(`${API}/admin/desk-dict-detect?incremental=false`, {}, authHeaders());
      setDetectResult(res.data);
      toast.success(res.data.message || "検出しました");
      await loadSyncStatus();
      await loadPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || "検出に失敗しました");
    } finally {
      setBusy2("");
    }
  };

  const resolvePending = async (action) => {
    const ids = [...selPending];
    if (ids.length === 0) { toast.error("対象を選択してください"); return; }
    const verb = action === "approve" ? "登録" : "「登録しない」に設定";
    if (!window.confirm(`選択した ${ids.length} 件を${verb}します。よろしいですか？`)) return;
    setBusy2(action);
    try {
      const res = await axios.post(`${API}/admin/dict-pending/resolve`, { ids, action }, authHeaders());
      toast.success(res.data.message || "処理しました");
      await loadPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || "処理に失敗しました");
    } finally {
      setBusy2("");
    }
  };

  const togglePending = (id) => {
    setSelPending((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAllPending = () => setSelPending(new Set(pending.map((p) => p.id)));

  const loadRejected = async () => {
    try {
      const res = await axios.get(`${API}/admin/dict-pending/rejected`, authHeaders());
      setRejected(res.data.items || []);
      setSelRejected(new Set());
    } catch (e) { /* ignore */ }
  };
  const toggleRejected = (id) => {
    setSelRejected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const restoreRejected = async () => {
    const ids = [...selRejected];
    if (ids.length === 0) { toast.error("対象を選択してください"); return; }
    if (!window.confirm(`選択した ${ids.length} 件を再提案に戻します。よろしいですか？`)) return;
    setBusy2("restore");
    try {
      const res = await axios.post(`${API}/admin/dict-pending/restore`, { ids }, authHeaders());
      toast.success(res.data.message || "戻しました");
      await loadRejected();
      await loadPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || "処理に失敗しました");
    } finally {
      setBusy2("");
    }
  };
  const openRejected = () => {
    const next = !showRejected;
    setShowRejected(next);
    if (next) loadRejected();
  };

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/desk/customers`, authHeaders());
      setList(res.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "デスク顧客の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
    loadSyncStatus();
    loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (c) => c.customer_code.toLowerCase().includes(q) || (c.customer_name || "").toLowerCase().includes(q)
    );
  }, [list, search]);

  const toggle = (code) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(code)) n.delete(code);
      else n.add(code);
      return n;
    });
  };
  const selectAllFiltered = () => setSelected((prev) => new Set([...prev, ...filtered.map((c) => c.customer_code)]));
  const clearSelection = () => setSelected(new Set());

  const run = async (kind, apply) => {
    const codes = [...selected];
    if (codes.length === 0) {
      toast.error("取り込む顧客を選択してください");
      return;
    }
    if (apply && !window.confirm(`選択した ${codes.length} 件をポータルへ反映します。よろしいですか？`)) return;
    const path = kind === "cust" ? "/admin/import/desk-customers" : "/admin/import/desk-dictionary";
    setBusy(`${kind}-${apply ? "apply" : "preview"}`);
    try {
      const res = await axios.post(`${API}${path}?apply=${apply}`, { codes }, authHeaders());
      if (kind === "cust") setCustResult(res.data);
      else setDictResult(res.data);
      toast.success(res.data.message || (apply ? "反映しました" : "確認しました"));
      if (apply && kind === "cust") loadCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "処理に失敗しました");
    } finally {
      setBusy("");
    }
  };

  const downloadCsv = (creds) => {
    const header = "顧客コード,顧客名,ログインID,パスワード";
    const lines = creds.map((c) => [c.code, c.store_name, c.username, c.password].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = "\uFEFF" + [header, ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "やさまるポータル_ログイン情報.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const printLetters = (creds) => {
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const letters = creds
      .map(
        (c) => `
      <section class="letter">
        <h1>やさまるポータル ログインID・パスワード発行のお知らせ</h1>
        <p>平素より、野菜の丸平をご利用いただき、誠にありがとうございます。</p>
        <p>このたび、ウェブ上から簡単にご注文いただける<b>「やさまるポータル」</b>のログイン情報を発行いたしました。<br/>下記のログインID・パスワードをご確認のうえ、ご利用ください。</p>
        <div class="box">
          <div class="box-title">【 お客様専用ログイン情報 】</div>
          <table>
            <tr><th>お客様名</th><td>${esc(c.store_name)} 様</td></tr>
            <tr><th>ログインID</th><td class="mono">${esc(c.username)}</td></tr>
            <tr><th>パスワード</th><td class="mono">${esc(c.password)}</td></tr>
            <tr><th>ログインページ</th><td>www.yasamaru-portal.online</td></tr>
          </table>
        </div>
        <div class="notes">
          <div class="notes-title">ご利用にあたってのお願い</div>
          <p>ログインIDとパスワードは、お客様専用の大切な情報です。<br/>第三者に知られないよう、紛失やお取り扱いにご注意ください。</p>
          <p>ログイン方法や操作方法が分からない場合は、担当者までお気軽にお問い合わせください。</p>
        </div>
        <div class="contact">
          <div>お問い合わせ先</div>
          <div>野菜の丸平／株式会社フレッシュエージェント</div>
          <div>TEL：03-3855-1610</div>
        </div>
        <p class="closing">今後とも、野菜の丸平をよろしくお願いいたします。</p>
      </section>`
      )
      .join("");
    const html = `<!doctype html><html lang="ja"><head><meta charset="utf-8"><title>ログイン通知書</title>
      <style>
        @page { size: A4; margin: 18mm; }
        * { box-sizing: border-box; }
        body { font-family: "Hiragino Kaku Gothic ProN","Yu Gothic","Meiryo",sans-serif; color:#1f2937; }
        .letter { page-break-after: always; }
        .letter:last-child { page-break-after: auto; }
        h1 { font-size: 18px; text-align:center; border-bottom:2px solid #059669; padding-bottom:8px; margin:0 0 18px; }
        p { font-size: 13px; line-height: 1.9; margin: 8px 0; }
        .box { border:1.5px solid #059669; border-radius:6px; padding:14px 18px; margin:18px 0; }
        .box-title { font-weight:bold; margin-bottom:10px; }
        table { width:100%; border-collapse:collapse; }
        th,td { text-align:left; padding:8px 6px; border-bottom:1px dashed #cbd5e1; font-size:14px; }
        th { width:130px; color:#374151; font-weight:600; }
        .mono { font-family: "Courier New", monospace; font-size:18px; font-weight:bold; letter-spacing:1px; }
        .notes { margin-top:16px; }
        .notes-title { font-weight:bold; margin-bottom:4px; }
        .contact { margin-top:20px; font-size:13px; line-height:1.8; }
        .closing { margin-top:16px; }
      </style></head><body>${letters}
      <script>window.onload=function(){setTimeout(function(){window.print();},300);};</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) {
      toast.error("ポップアップがブロックされました。ブロックを解除してください");
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="space-y-6" data-testid="desk-customer-import">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">デスクから顧客・辞書を取込</h1>
        <p className="text-sm text-slate-600 mt-1">
          取り込みたい顧客だけを選んで、納品先(店舗)の作成/更新と、顧客辞書からの店舗別商品設定への反映ができます。
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-wrap items-center gap-3" data-testid="desk-resend-cancels">
        <div className="text-sm text-amber-900 flex-1 min-w-[260px]">
          <b>デスクに残っている取消の再送</b>：以前、伝票の一部だけを取消した分がデスクに届いていませんでした。今日以降の納品分を、もう一度デスクへ送ります（何度押しても問題ありません）。
        </div>
        <Button
          size="sm"
          variant="outline"
          data-testid="desk-resend-cancels-btn"
          className="border-amber-500 text-amber-800 hover:bg-amber-100"
          onClick={async () => {
            try {
              const r = await axios.post(`${API}/admin/desk/resend-partial-cancellations`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
              toast.success(`取消 ${r.data.resent}件をデスクへ再送しました（次回のデスク取込で反映）`);
            } catch (e) {
              toast.error(e.response?.data?.detail || "再送に失敗しました");
            }
          }}
        >
          <RefreshCw className="h-4 w-4 mr-1.5" />取消を再送
        </Button>
      </div>

      {/* 顧客選択 */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="顧客コード・顧客名で検索"
              className="pl-8 h-9"
              data-testid="desk-cust-search"
            />
          </div>
          <Button onClick={selectAllFiltered} variant="outline" size="sm" data-testid="desk-cust-selectall">
            表示中を全選択
          </Button>
          <Button onClick={clearSelection} variant="outline" size="sm" data-testid="desk-cust-clear">
            選択解除
          </Button>
          <Button onClick={loadCustomers} variant="outline" size="sm" disabled={loading} data-testid="desk-cust-reload">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <span className="text-sm text-slate-600 ml-auto">
            選択中 <b className="text-emerald-700" data-testid="desk-cust-selected-count">{selected.size}</b> / 全 {list.length} 件
          </span>
        </div>

        <div className="max-h-[22rem] overflow-y-auto border border-slate-100 rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs sticky top-0">
              <tr>
                <th className="px-3 py-2 w-10"></th>
                <th className="px-3 py-2 text-left">顧客コード</th>
                <th className="px-3 py-2 text-left">顧客名</th>
                <th className="px-3 py-2 text-left">ランク</th>
                <th className="px-3 py-2 text-left">グループ</th>
                <th className="px-3 py-2 text-center">状態</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.customer_code}
                  className="border-t border-slate-100 hover:bg-emerald-50/40 cursor-pointer"
                  onClick={() => toggle(c.customer_code)}
                  data-testid={`desk-cust-row-${c.customer_code}`}
                >
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(c.customer_code)}
                      onCheckedChange={() => toggle(c.customer_code)}
                      data-testid={`desk-cust-check-${c.customer_code}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{c.customer_code}</td>
                  <td className="px-3 py-2 text-slate-800">{c.customer_name}</td>
                  <td className="px-3 py-2">{c.rank}</td>
                  <td className="px-3 py-2 text-slate-600">{c.group_name}</td>
                  <td className="px-3 py-2 text-center">
                    {c.already ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">登録済</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">未登録</span>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                    {loading ? "読み込み中..." : "該当する顧客がありません"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* アクション */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="import-customers">
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-5 w-5 text-emerald-600" />
            <h2 className="text-base font-semibold text-slate-800">① 選択顧客を取込（納品先）</h2>
          </div>
          <p className="text-sm text-slate-600">
            選択した顧客を店舗(納品先)として作成/更新。既存店舗は名称・ランク・住所等のみ更新します。
          </p>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => run("cust", false)} disabled={!!busy} variant="outline" size="sm" data-testid="import-customers-preview">
              {busy === "cust-preview" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
              確認
            </Button>
            <Button onClick={() => run("cust", true)} disabled={!!busy || !custResult} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="import-customers-apply">
              <Send className="h-4 w-4 mr-1" />反映する
            </Button>
          </div>
          {custResult && (
            <div className="mt-3 rounded-md bg-slate-50 border border-slate-100 p-3 text-sm" data-testid="import-customers-summary">
              <div>対象 {custResult.to_create + custResult.to_update} 件（新規 <b className="text-emerald-700">{custResult.to_create}</b> / 更新 <b>{custResult.to_update}</b>）</div>
              <div className="mt-0.5">ログイン{custResult.apply ? "発行" : "発行予定"}: <b className="text-emerald-700">{custResult.login_new}</b> 件</div>
              {(custResult.credentials || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button onClick={() => printLetters(custResult.credentials)} size="sm" variant="outline" data-testid="print-login-letters">
                    <Printer className="h-4 w-4 mr-1" />
                    ログイン通知書を印刷 / PDF{custResult.apply ? "" : "（プレビュー）"}
                  </Button>
                  <Button onClick={() => downloadCsv(custResult.credentials)} size="sm" variant="outline" data-testid="download-login-csv">
                    <Download className="h-4 w-4 mr-1" />
                    ログイン情報CSV
                  </Button>
                </div>
              )}
              {!custResult.apply && (custResult.credentials || []).length > 0 && (
                <div className="text-xs text-amber-700 mt-2">※ これはプレビューです。実際に発行するには「反映する」を押してください。</div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="import-dictionary">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="h-5 w-5 text-emerald-600" />
            <h2 className="text-base font-semibold text-slate-800">② 選択顧客の辞書を取込（商品設定）</h2>
          </div>
          <p className="text-sm text-slate-600">
            選択顧客の取扱商品を商品コードで突合し、店舗別商品設定へ追加(マージ)します。先に①で店舗作成が必要です。
          </p>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => run("dict", false)} disabled={!!busy} variant="outline" size="sm" data-testid="import-dictionary-preview">
              {busy === "dict-preview" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
              確認
            </Button>
            <Button onClick={() => run("dict", true)} disabled={!!busy || !dictResult} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="import-dictionary-apply">
              <Send className="h-4 w-4 mr-1" />反映する
            </Button>
          </div>
          {dictResult && (
            <div className="mt-3 rounded-md bg-slate-50 border border-slate-100 p-3 text-sm" data-testid="import-dictionary-summary">
              <div>更新店舗 <b className="text-emerald-700">{dictResult.stores_updated}</b>{dictResult.customers_without_store ? ` / 店舗未作成 ${dictResult.customers_without_store}` : ""}</div>
              {dictResult.apply && (
                <div className="mt-0.5">FAX履歴に登録: <b className="text-emerald-700">{dictResult.fax_recorded || 0}</b> 件（{dictResult.fax_stores || 0} 店舗）</div>
              )}
              {dictResult.unmatched_product_codes > 0 && (
                <div className="text-amber-700 text-xs mt-1">商品コード未突合 {dictResult.unmatched_product_codes}件 — 先に商品同期をご確認ください</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 既存顧客の兄弟単位を一括補完 */}
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="backfill-siblings">
        <div className="flex items-center gap-2 mb-1">
          <RefreshCw className="h-5 w-5 text-emerald-600" />
          <h2 className="text-base font-semibold text-slate-800">既存顧客の単位グループを一括補完</h2>
        </div>
        <p className="text-sm text-slate-600">
          辞書に「きゅうり 本」だけ入っている顧客に、同じ商品の別単位「きゅうり kg」も自動で追加します（取扱商品とFAX履歴の両方）。今後の辞書取込では自動で補完されますが、過去分をまとめて補完したいときに使います。
        </p>
        <div className="flex gap-2 mt-4">
          <Button onClick={() => runBackfill(false)} disabled={!!busy} variant="outline" size="sm" data-testid="backfill-preview-btn">
            {busy === "backfill-preview" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
            確認（対象件数）
          </Button>
          <Button onClick={() => runBackfill(true)} disabled={!!busy || !backfill} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="backfill-apply-btn">
            <Send className="h-4 w-4 mr-1" />補完を実行
          </Button>
        </div>
        {backfill && (
          <div className="mt-3 rounded-md bg-slate-50 border border-slate-100 p-3 text-sm" data-testid="backfill-summary">
            <div>対象店舗 <b className="text-emerald-700">{backfill.stores_affected}</b> / 補完商品 <b className="text-emerald-700">{backfill.products_added}</b> 件{backfill.apply ? "（実行済み）" : "（未実行）"}</div>
            {(backfill.samples || []).slice(0, 5).map((s) => (
              <div key={s.code} className="text-xs text-slate-500 mt-0.5">
                {s.code} {s.name}: +{s.added}件{s.added_names?.length ? `（例: ${s.added_names.slice(0, 3).join("、")}）` : ""}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* デスクの補助表示を商品へ取り込み */}
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="subdisplay-import">
        <div className="flex items-center gap-2 mb-1">
          <BookOpen className="h-5 w-5 text-amber-600" />
          <h2 className="text-base font-semibold text-slate-800">デスクの「補助表示」を商品に取り込み</h2>
        </div>
        <p className="text-sm text-slate-600">
          デスク商品の「補助表示（display_hint）」を商品マスタに取り込みます。店舗別商品設定の商品一覧に、商品名の横にオレンジのバッジで表示され（例: 「若菜専用」）、特定顧客向けの特殊コードを見分けやすくなります。デスク商品同期でも反映されますが、こちらは今ある商品へすぐ反映します。
        </p>
        <div className="flex gap-2 mt-4">
          <Button onClick={() => runSubImport(false)} disabled={!!busy} variant="outline" size="sm" data-testid="subdisplay-preview-btn">
            {busy === "sub-preview" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
            確認（対象件数）
          </Button>
          <Button onClick={() => runSubImport(true)} disabled={!!busy || !subImport} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="subdisplay-apply-btn">
            <Send className="h-4 w-4 mr-1" />取り込みを実行
          </Button>
        </div>
        {subImport && (
          <div className="mt-3 rounded-md bg-slate-50 border border-slate-100 p-3 text-sm" data-testid="subdisplay-summary">
            <div>更新対象 <b className="text-amber-700">{subImport.products_updated}</b> 件 / 補助表示あり <b className="text-amber-700">{subImport.products_with_hint}</b> 件（デスク商品 {subImport.desk_products} 件）{subImport.apply ? "（実行済み）" : "（未実行）"}</div>
            {(subImport.samples || []).slice(0, 5).map((s) => (
              <div key={s.code} className="text-xs text-slate-500 mt-0.5">
                {s.code} {s.name}: <span className="text-amber-700 font-medium">{s.sub_display}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* デスク辞書の未反映変更（検出→確認して登録/登録しない） */}
      <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="dict-sync-all">
        <div className="flex items-center gap-2 mb-1">
          <RefreshCw className="h-5 w-5 text-emerald-600" />
          <h2 className="text-base font-semibold text-slate-800">デスク辞書の未反映変更（確認して登録）</h2>
        </div>
        <p className="text-sm text-slate-600">
          顧客辞書に増えた商品を検出して下の一覧に表示します。内容を確認し、店舗別商品設定に「登録する」か「登録しない」かを選べます（自動では反映されません）。
        </p>
        <div className="text-xs text-slate-500 mt-2" data-testid="dict-sync-status">
          {syncStatus?.last_run_at_jst ? (
            <>最終検出: {new Date(syncStatus.last_run_at_jst).toLocaleString("ja-JP")}
              {syncStatus.last_result ? `（新規検出 ${syncStatus.last_result.new_pending} / 未突合 ${syncStatus.last_result.unmatched_product_codes}）` : ""}</>
          ) : ("まだ検出されていません")}
        </div>
        <div className="flex flex-wrap gap-2 mt-4 items-center">
          <Button onClick={runDetect} disabled={!!busy2} size="sm" variant="outline" data-testid="dict-detect-now">
            {busy2 === "detect" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
            今すぐ差分を検出
          </Button>
          <span className="text-xs text-slate-400">※ 毎朝5:00(日本時間)に自動検出（自動では登録しません）</span>
        </div>

        {/* 未反映一覧 */}
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-sm font-semibold text-slate-700">未反映の追加候補</span>
            <span className="text-sm text-slate-500">選択 <b className="text-emerald-700" data-testid="pending-selected">{selPending.size}</b> / {pending.length} 件</span>
            <Button onClick={selectAllPending} disabled={pending.length === 0} variant="outline" size="sm" data-testid="pending-selectall">全選択</Button>
            <Button onClick={() => setSelPending(new Set())} disabled={selPending.size === 0} variant="outline" size="sm" data-testid="pending-clear">解除</Button>
            <div className="ml-auto flex gap-2">
              <Button onClick={() => resolvePending("approve")} disabled={!!busy2 || selPending.size === 0} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pending-approve">
                <Send className="h-4 w-4 mr-1" />選択を登録する
              </Button>
              <Button onClick={() => resolvePending("reject")} disabled={!!busy2 || selPending.size === 0} size="sm" variant="outline" className="border-slate-400 text-slate-600" data-testid="pending-reject">
                登録しない
              </Button>
            </div>
          </div>
          <div className="max-h-[24rem] overflow-y-auto border border-slate-100 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs sticky top-0">
                <tr>
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-3 py-2 text-left">店舗コード</th>
                  <th className="px-3 py-2 text-left">店舗名</th>
                  <th className="px-3 py-2 text-left">商品コード</th>
                  <th className="px-3 py-2 text-left">商品名</th>
                  <th className="px-3 py-2 text-left">検出日時</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-emerald-50/40 cursor-pointer" onClick={() => togglePending(p.id)} data-testid={`pending-row-${p.id}`}>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selPending.has(p.id)} onCheckedChange={() => togglePending(p.id)} data-testid={`pending-check-${p.id}`} />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{p.store_code}</td>
                    <td className="px-3 py-2 text-slate-800">{p.store_name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.product_code}</td>
                    <td className="px-3 py-2 text-slate-800">{p.product_name}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{p.detected_at ? new Date(p.detected_at).toLocaleString("ja-JP") : ""}</td>
                  </tr>
                ))}
                {pending.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">未反映の追加候補はありません</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 却下済み（登録しない）の管理・復活 */}
        <div className="mt-4 border-t border-slate-100 pt-3">
          <button
            onClick={openRejected}
            className="text-sm text-slate-600 hover:text-slate-800 underline"
            data-testid="toggle-rejected"
          >
            {showRejected ? "却下済み（登録しない）を隠す" : "却下済み（登録しない）を表示・復活する"}
          </button>
          {showRejected && (
            <div className="mt-3">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-sm text-slate-500">選択 <b className="text-emerald-700">{selRejected.size}</b> / {rejected.length} 件</span>
                <Button onClick={() => setSelRejected(new Set(rejected.map((r) => r.id)))} disabled={rejected.length === 0} variant="outline" size="sm" data-testid="rejected-selectall">全選択</Button>
                <Button onClick={() => setSelRejected(new Set())} disabled={selRejected.size === 0} variant="outline" size="sm">解除</Button>
                <Button onClick={restoreRejected} disabled={!!busy2 || selRejected.size === 0} size="sm" className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="rejected-restore">
                  <RefreshCw className="h-4 w-4 mr-1" />再提案に戻す
                </Button>
              </div>
              <div className="max-h-[18rem] overflow-y-auto border border-slate-100 rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs sticky top-0">
                    <tr>
                      <th className="px-3 py-2 w-10"></th>
                      <th className="px-3 py-2 text-left">店舗コード</th>
                      <th className="px-3 py-2 text-left">店舗名</th>
                      <th className="px-3 py-2 text-left">商品コード</th>
                      <th className="px-3 py-2 text-left">商品名</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rejected.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => toggleRejected(r.id)} data-testid={`rejected-row-${r.id}`}>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={selRejected.has(r.id)} onCheckedChange={() => toggleRejected(r.id)} data-testid={`rejected-check-${r.id}`} />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{r.store_code}</td>
                        <td className="px-3 py-2 text-slate-800">{r.store_name}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.product_code}</td>
                        <td className="px-3 py-2 text-slate-800">{r.product_name}</td>
                      </tr>
                    ))}
                    {rejected.length === 0 && (
                      <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">却下済みの候補はありません</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
